import {VideoJsPlayer} from 'video.js';
import 'videojs-contrib-ads';
import 'videojs-ima';
import type {MutableRefObject} from 'react';
import {setAdCredits} from 'app/helpers/adStepper';
import VideoEventProvider from 'app/components/tv/events/VideoEventProvider';
import AdFinishedEvent from 'app/components/tv/events/AdFinishedEvent';
import {captureSentryExceptionAndThrowLocal} from 'app/services/sentry/sentryLogging';
import type {ImaOptions} from 'app/components/tv/models/ImaTypes';
import Video from 'app/components/tv/models/video';
import VideoAdsInitializedEvent from 'app/components/tv/events/VideoAdsInitializedEvent';
import Device from 'app/services/Device';
import AdKeywords, {AdKeywordsInterface, NativeVASTKeywords} from 'app/components/tv/models/AdKeywords';
import RequestAdsException from 'app/exceptions/RequestAdsException';
import {SelectedDistrictPartialReduxState} from 'app/components/tv/apiResources/DistrictResource';
import {LayoutMode} from 'app/styles/LayoutMode';

export type SetAdPlaying = (value: ((prevState: boolean) => boolean) | boolean) => void;
export type SetRemainingTime = (value: ((prevState: string | null) => string | null) | string | null) => void;

const AutoplayPreventedCode = 1205;
const NoVastResponseCode = 303;
const AdServerErrorCode = 1005;
const LoadingTimeoutCode = 402;

export default class VideoAds {
  private readonly player: VideoJsPlayer;
  private wrapperDiv: HTMLElement | null = null;
  private prebidVASTUrl: string;
  private adKeywords: AdKeywordsInterface;
  private adCreditsRef: MutableRefObject<number>;

  constructor(
    player: VideoJsPlayer,
    firstVideo: Video,
    keywords: {
      userDistrict: SelectedDistrictPartialReduxState;
      nativeVASTKeywords: NativeVASTKeywords;
      layout: LayoutMode;
    },
    adCreditsRef: MutableRefObject<number>
  ) {
    this.player = player;
    this.adCreditsRef = adCreditsRef;
    this.setAdKeywords(firstVideo, keywords);
    this.initializeAdContainer = this.initializeAdContainer.bind(this);
    if (!this.player) {
      throw new Error('A Videojs-Player instance is required to setup video-ads.');
    }
  }

  async setup(
    setAdPlaying: SetAdPlaying,
    setRemainingTime: SetRemainingTime,
    isFirstPage: boolean,
    ppid?: string
  ): Promise<void> {
    await this.setVASTUrl();
    this.setupImaPlugin(setAdPlaying, setRemainingTime, ppid);
    this.player.on('adserror', this.handleAdsError);
    this.scheduleAdContainerInitialization(isFirstPage);
  }

  requestAds() {
    this.player.ima.changeAdTag(this.prebidVASTUrl);
    try {
      this.player.ima.requestAds();
    } catch (e) {
      throw new RequestAdsException('Calling requestAds() failed somewhere inside videojs-ima code');
    }
  }

  dispose() {
    this.player.off('adserror', this.handleAdsError);
    this.player.ima.controller.sdkImpl.adDisplayContainer.destroy();
    this.player.ima.controller.reset();
  }

  private setupImaPlugin(setAdPlaying: SetAdPlaying, setRemainingTime: SetRemainingTime, ppid?: string) {
    const imaOptions: ImaOptions = {
      autoPlayAdBreaks: false,
      showCountdown: false,
      disableCustomPlaybackForIOS10Plus: true,
      locale: 'de',
      ppid: ppid,
      adsManagerLoadedCallback: () => this.handleAdsManagerLoadedCallback(setAdPlaying, setRemainingTime),
    };

    this.player.ima(imaOptions);
  }

  private scheduleAdContainerInitialization(isFirstPage: boolean): void {
    if (!isFirstPage) {
      this.initializeAdContainer();
      return;
    }
    this.removeControlsIPadAndAndroid();
    this.wrapperDiv = document.getElementById('content_video');
    this.addInitEventListeners();
  }

  private addInitEventListeners() {
    if (this.wrapperDiv) {
      this.wrapperDiv.addEventListener('click', this.initializeAdContainer);
      if (new Device().isTouchDevice()) {
        this.wrapperDiv.addEventListener('touchend', this.initializeAdContainer);
      }
    }
    window.addEventListener('keyup', this.initializeAdContainer);
  }

  private removeInitEventListeners(): void {
    if (this.wrapperDiv) {
      this.wrapperDiv.removeEventListener('click', this.initializeAdContainer);
      this.wrapperDiv.removeEventListener('touchend', this.initializeAdContainer);
    }
    window.removeEventListener('keyup', this.initializeAdContainer);
  }

  private initializeAdContainer(): void {
    this.player.ima.initializeAdDisplayContainer();
    this.removeInitEventListeners();
    this.dispatchInitializedEvent();
  }

  private dispatchInitializedEvent(): void {
    VideoEventProvider.getInstance().dispatch(new VideoAdsInitializedEvent());
  }

  private removeControlsIPadAndAndroid() {
    // Remove controls from the player on iPad and Android to stop native controls from stealing our click
    const contentPlayer = document.getElementById('content_video_html5_api');
    if (!contentPlayer) {
      throw new Error('No content player found');
    }
    const device = new Device();
    if ((device.isIPad() || device.isAndroid()) && contentPlayer.hasAttribute('controls')) {
      contentPlayer.removeAttribute('controls');
    }
  }

  private handleAdsManagerLoadedCallback = (setAdPlaying: SetAdPlaying, setRemainingTime: SetRemainingTime) => {
    this.player.on('playerresize', this.handleResize);

    this.player.ima.playAdBreak();

    this.player.ima.addEventListener(window.google.ima.AdEvent.Type.CONTENT_RESUME_REQUESTED, () =>
      this.handleContentResumeRequested(setAdPlaying)
    );
    this.player.ima.addEventListener(window.google.ima.AdEvent.Type.AD_PROGRESS, event =>
      this.handleAdProgress(event, setRemainingTime)
    );
    this.player.ima.addEventListener(window.google.ima.AdEvent.Type.STARTED, () => this.onAdStarted(setAdPlaying));
  };

  private handleResize = () => {
    const adsManager = this.player.ima.getAdsManager();
    const {width, height} = this.player.currentDimensions();
    adsManager.resize(width, height, window.google.ima.ViewMode.NORMAL);
  };

  private handleContentResumeRequested = async (setAdPlaying: SetAdPlaying) => {
    this.player.off('playerresize', this.handleResize);
    setAdCredits(this.adCreditsRef);
    setAdPlaying(false);
    VideoEventProvider.getInstance().dispatch(new AdFinishedEvent());

    await this.setVASTUrl();
  };

  private onAdStarted = (setAdPlaying: SetAdPlaying) => {
    setAdPlaying(true);
  };

  private handleAdProgress = (e: any, setRemainingTime: SetRemainingTime) => {
    const progressData = e.getAdData();
    if (progressData) {
      const countdown = progressData.adBreakDuration - Math.floor(progressData.currentTime);
      const remainingTime = countdown < 10 ? '0' + countdown.toString() : countdown.toString();
      setRemainingTime(remainingTime);
    }
  };

  private handleAdsError = (error: any) => {
    this.logAdError(error, this.player.currentSrc());
    setAdCredits(this.adCreditsRef, 1);
    VideoEventProvider.getInstance().dispatch(new AdFinishedEvent());
  };

  private logAdError = (error: any, src: string): void => {
    const adError = error.data?.AdError;
    if (!adError) {
      captureSentryExceptionAndThrowLocal(new Error('Ads Error without additional info'), {
        extra: {
          errorInfo: error,
          src,
        },
      });
      return;
    }
    const innerError = adError.getInnerError();
    const errorCode = adError.getErrorCode();
    if (innerError?.errorCode === AutoplayPreventedCode || errorCode === AutoplayPreventedCode) {
      return;
    }
    if (errorCode === NoVastResponseCode) {
      // expected error as there is no 100% fillrate
      return;
    }
    if (errorCode === AdServerErrorCode) {
      // there can always be an unexpected ad server error
      return;
    }
    if (errorCode === LoadingTimeoutCode) {
      // there can always be a timeout loading a video-ad
      return;
    }
    captureSentryExceptionAndThrowLocal(new Error('Ads Error with additional info'), {
      extra: {
        errorInfo: {
          errorCode,
          innerError,
          vastErrorCode: adError.getVastErrorCode(),
          toString: adError.toString(),
        },
        src,
      },
    });
  };

  private async getAdTagUrl(keywords: AdKeywordsInterface) {
    return await new Promise<string>((resolve, reject) => {
      try {
        window.requestVideoBids(keywords.region, keywords, (vastRequest?: string) => {
          if (vastRequest) {
            resolve(vastRequest);
          } else {
            reject(new Error('Failed to retrieve vastRequest from requestVideoBids'));
          }
        });
      } catch (error) {
        reject(error);
      }
    });
  }

  private setAdKeywords(
    video: Video,
    keywords: {
      userDistrict: SelectedDistrictPartialReduxState;
      nativeVASTKeywords: NativeVASTKeywords;
      layout: LayoutMode;
    }
  ) {
    this.adKeywords = AdKeywords.combine(video, keywords);
  }

  async setVASTUrl() {
    try {
      this.prebidVASTUrl = await this.getAdTagUrl(this.adKeywords);
    } catch (error) {
      captureSentryExceptionAndThrowLocal(error, {adKeywords: this.adKeywords});
    }
  }
}
